import { z } from 'zod';
import { BREAKDOWN_PANEL_CATEGORIES, ElementCategory } from './element.js';
import { ConfidenceBand, ConfidenceReason } from './requirement.js';

/**
 * The extraction output contract.
 *
 * EVIDENCE COMES FIRST in the field order, deliberately. A model asked for a
 * conclusion and then its evidence will confabulate support for whatever it
 * already said; asked to quote first, the conclusion is constrained by the text
 * it just produced. The order is part of the contract, not a formatting
 * preference.
 */

/** No floats anywhere. A band with a closed reason is actionable — "NEEDS REVIEW
 *  — interior/exterior unclear" tells a producer what to look at — where 0.63
 *  tells them nothing and invites false precision. */
export const SceneLocationSignal = z.object({
  /** The verbatim screenplay text supporting this signal. Re-located against the
   *  canonical text before any highlight renders; the model's own offsets are
   *  never trusted. */
  evidenceQuote: z.string().min(1).max(2000),

  /** Constrained to the vocabulary sent with the request. This prevents
   *  out-of-vocabulary codes; it CANNOT prevent a semantically wrong choice,
   *  which is what the human review layer exists for. */
  typeCode: z.string().min(1).max(64),
  subtypeSlugs: z.array(z.string().max(64)).max(8).default([]),
  vibeCodes: z.array(z.string().max(64)).max(8).default([]),

  intExt: z.enum(['INT', 'EXT', 'INT_EXT', 'UNKNOWN']).default('UNKNOWN'),
  timeOfDay: z.string().max(32).nullable().default(null),

  /** Free text the producer reads, never machine-evaluated. */
  creativeDescription: z.string().max(2000).nullable().default(null),

  confidenceBand: ConfidenceBand,
  confidenceReason: ConfidenceReason.nullable().default(null),
});
export type SceneLocationSignal = z.infer<typeof SceneLocationSignal>;

export const SceneExtractionOutput = z.object({
  sceneId: z.string().min(1),
  signals: z.array(SceneLocationSignal).max(10),
});
export type SceneExtractionOutput = z.infer<typeof SceneExtractionOutput>;

/**
 * One LOCATION, with every scene that needs it.
 *
 * WHY THE WHOLE SCREENPLAY GOES IN ONE REQUEST (measured 2026-09-18).
 *
 * Extraction used to ask the model about one scene at a time, which cannot see
 * that scene 1 and scene 74 are the same apartment — so grouping fell to a
 * regex over sluglines, which only ever catches literal repeats. Sending the
 * whole screenplay instead, on a real 120-scene feature:
 *
 *     one request · 9.4s · 38,481 tokens in · 3,537 out (5% of the cap)
 *     53 distinct locations, 13 of them spanning several scenes
 *     "David's New York City Apartment" -> scenes 1, 14, 19, 23, 50, 74, 76, 101, 102
 *
 * against 120 requests and no grouping at all. The output is SMALLER, not
 * larger, because grouping compresses it — 53 locations rather than 120 scene
 * entries. The estimate that said otherwise was wrong by an order of magnitude,
 * which is why the numbers above are recorded rather than reasoned about.
 *
 * `sceneRefs` are the ordinals of OUR OWN scene markers, injected into the text
 * we send. The screenplay's own numbering is unreliable — a shooting draft
 * renumbers, a first draft has none — so the reference is to a position we
 * control and can map back exactly.
 */
export const ScreenplayLocation = z.object({
  /** Evidence first: see the note at the top of this file. */
  evidenceQuote: z.string().min(1).max(2000),

  /** The scene markers this location appears in, as sent. */
  sceneRefs: z.array(z.number().int().positive()).min(1).max(400),

  name: z.string().min(1).max(255),
  typeCode: z.string().min(1).max(64),
  subtypeSlugs: z.array(z.string().max(64)).max(8).default([]),
  vibeCodes: z.array(z.string().max(64)).max(8).default([]),
  intExt: z.enum(['INT', 'EXT', 'INT_EXT', 'UNKNOWN']).default('UNKNOWN'),
  creativeDescription: z.string().max(2000).nullable().default(null),

  confidenceBand: ConfidenceBand,
  confidenceReason: ConfidenceReason.nullable().default(null),
});
export type ScreenplayLocation = z.infer<typeof ScreenplayLocation>;

export const ScreenplayExtractionOutput = z.object({
  /** Capped well above any real feature: 53 came back for a 120-scene script. */
  locations: z.array(ScreenplayLocation).max(300),
});
export type ScreenplayExtractionOutput = z.infer<typeof ScreenplayExtractionOutput>;

/**
 * Bumped whenever this shape changes. It is a component of the extraction cache
 * key, so an old cached response can never be replayed against a new schema and
 * silently validated into the wrong shape.
 */
export const OUTPUT_SCHEMA_VERSION = '2';

/**
 * WHICH ELEMENT CATEGORIES THE MODEL IS ASKED FOR: the seventeen of the breakdown panel.
 *
 * The scene's breakdown is asked for whole — its location and every department — in
 * one request per small group of scenes (owner decision, 2026-11-03), because a 1st AD
 * reads a scene once for all of it and the model is reading the scene either way.
 * Attributing a bad answer to the rule that produced it is the evaluation's job, which
 * scores each category on its own (`src/scripts/eval-elements.ts`).
 *
 * CAST is asked for too. The character cues still READ cast structurally
 * (`parsers/character-cues.ts`), which is what Day Out of Days is paid against; the
 * model adds who is present without speaking, and reconciling the two is the
 * pipeline's job when this is wired in.
 */
export const MODEL_ELEMENT_CATEGORIES: readonly ElementCategory[] = BREAKDOWN_PANEL_CATEGORIES;

/**
 * Why an element is not HIGH. Closed, like a location's reason, so a reviewer is
 * told what to look at rather than handed a number.
 *
 * - ON_CAMERA_UNCLEAR   the script implies it but does not say it is seen
 *                       ("she gets out" with no car described)
 * - GENERIC_BACKGROUND  unnamed atmosphere: traffic, a row of parked cars
 * - CATEGORY_AMBIGUOUS  it may belong to another department (a toy car, a prop)
 * - SAME_ELEMENT_UNCLEAR it may be one already named in another scene, or not
 * - OTHER
 */
export const ElementConfidenceReason = z.enum([
  'ON_CAMERA_UNCLEAR',
  'GENERIC_BACKGROUND',
  'CATEGORY_AMBIGUOUS',
  'SAME_ELEMENT_UNCLEAR',
  'OTHER',
  // Set by the server, never asked of the model (skill breakdown, 2026-11-03):
  // DIALOGUE_ONLY        its evidence is in dialogue: talked about, not necessarily on set
  // EVIDENCE_RELOCATED   the quote is not in the block the model named, but elsewhere in the scene
  // EVIDENCE_AMBIGUOUS   the quote occurs more than once, so which occurrence is meant is open
  // DEPENDENCY_REVIEW    a skill dependency (REQUIRE_REVIEW) sends it to a person
  'DIALOGUE_ONLY',
  'EVIDENCE_RELOCATED',
  'EVIDENCE_AMBIGUOUS',
  'DEPENDENCY_REVIEW',
]);

/** The reasons a model may give; the rest are the server's (`ElementConfidenceReason`). */
export const MODEL_ELEMENT_CONFIDENCE_REASONS = [
  'ON_CAMERA_UNCLEAR',
  'GENERIC_BACKGROUND',
  'CATEGORY_AMBIGUOUS',
  'SAME_ELEMENT_UNCLEAR',
  'OTHER',
] as const;
export type ElementConfidenceReason = z.infer<typeof ElementConfidenceReason>;

/**
 * ONE ELEMENT IN ONE SCENE, as the model proposes it inside the scene's entry.
 *
 * PER SCENE, NOT GROUPED like a location. A location is booked once for every
 * scene that needs it, so the grouping is the answer; an element is tagged on each
 * scene it appears in, and each tag needs its OWN quote from that scene — a quote
 * from scene 4 is no evidence that the car is in scene 90. The same element in two
 * scenes is two entries with the same name, and `normalizeElementName` makes them
 * one `element` row.
 *
 * Evidence first, for the reason at the top of this file.
 */
export const SceneBreakdownElement = z.object({
  evidenceQuote: z.string().min(1).max(2000),
  category: ElementCategory,
  /** As the script calls it, and the same name in every scene it appears in. */
  name: z.string().min(1).max(255),
  confidenceBand: ConfidenceBand,
  confidenceReason: ElementConfidenceReason.nullable().default(null),
});
export type SceneBreakdownElement = z.infer<typeof SceneBreakdownElement>;

/**
 * THE PLACE THE CAMERA IS IN, for one scene.
 *
 * Not yet a location requirement: grouping the scenes that need the same place (the
 * apartment of scene 1 and of scene 74) is a later pass over these readings, which a
 * small group of scenes cannot see.
 */
export const SceneBreakdownLocation = z.object({
  evidenceQuote: z.string().min(1).max(2000),
  name: z.string().min(1).max(255),
  /** Constrained to the vocabulary sent with the request, as `ScreenplayLocation.typeCode`. */
  typeCode: z.string().min(1).max(64),
  intExt: z.enum(['INT', 'EXT', 'INT_EXT', 'UNKNOWN']).default('UNKNOWN'),
  confidenceBand: ConfidenceBand,
  confidenceReason: ConfidenceReason.nullable().default(null),
});
export type SceneBreakdownLocation = z.infer<typeof SceneBreakdownLocation>;

/**
 * ONE SCENE'S BREAKDOWN: an entry for EVERY scene sent, even one with nothing in it.
 *
 * The entry is what tells "read, and empty" from "skipped": a model shown five scenes
 * that answers about three has not said the other two are empty, and without an entry
 * per scene the two would look the same.
 */
export const SceneBreakdown = z.object({
  /** The `[[S…]]` marker of the scene, as sent. */
  sceneRef: z.number().int().positive(),
  location: SceneBreakdownLocation.nullable().default(null),
  elements: z.array(SceneBreakdownElement).max(300).default([]),
});
export type SceneBreakdown = z.infer<typeof SceneBreakdown>;

export const ElementExtractionOutput = z.object({
  scenes: z.array(SceneBreakdown).max(400),
});
export type ElementExtractionOutput = z.infer<typeof ElementExtractionOutput>;

/**
 * An element with the scene it was found in — the shape every consumer reads, flattened
 * from the scene entries by the adapters (`keepAnsweredElements`).
 */
export const ScreenplayElement = SceneBreakdownElement.extend({
  sceneRef: z.number().int().positive(),
});
export type ScreenplayElement = z.infer<typeof ScreenplayElement>;

/** A scene's location reading with the scene it belongs to. */
export const ScreenplaySceneLocation = SceneBreakdownLocation.extend({
  sceneRef: z.number().int().positive(),
});
export type ScreenplaySceneLocation = z.infer<typeof ScreenplaySceneLocation>;

/**
 * The element answer's own version, separate from the location answer's. 2: an entry
 * per scene with its location (was one flat list of vehicles).
 */
export const ELEMENT_OUTPUT_SCHEMA_VERSION = '2';

/**
 * THE SKILL BREAKDOWN'S ANSWER (2026-11-03): an entry per scene, each element pointing at the
 * BLOCK of the scene it was read in (`⟦b3⟧`, `parsers/blocks.ts`) with a short quote from it. The
 * server finds the quote in that block, rebuilds the exact text and offsets, and decides the
 * evidence status (`modules/extraction/skills/evidence.ts`); the model's own offsets are never
 * asked for. `quantity` is the count the script states, or null; it is a hint for a person, never a
 * confirmed count.
 */
export const SkillAnswerElement = z.object({
  block: z.string().min(1).max(16),
  quote: z.string().min(1).max(600),
  category: ElementCategory,
  name: z.string().min(1).max(255),
  /**
   * A count is a hint (owner decision 2026-11-03), so a count the model gives as 0 or less reads as
   * no count rather than refusing the whole answer: on 2026-11-04 one element's `quantity: 0` cost
   * a request its other 21 elements.
   */
  quantity: z
    .number()
    .int()
    .max(100_000)
    .nullable()
    .default(null)
    .transform((q) => (q !== null && q > 0 ? q : null)),
  confidenceBand: ConfidenceBand,
  confidenceReason: ElementConfidenceReason.nullable().default(null),
});
export type SkillAnswerElement = z.infer<typeof SkillAnswerElement>;

export const SkillSceneAnswer = z.object({
  sceneRef: z.number().int().positive(),
  elements: z.array(SkillAnswerElement).max(300).default([]),
});
export type SkillSceneAnswer = z.infer<typeof SkillSceneAnswer>;

export const SkillExtractionOutput = z.object({
  scenes: z.array(SkillSceneAnswer).max(400),
});
export type SkillExtractionOutput = z.infer<typeof SkillExtractionOutput>;

export const SKILL_OUTPUT_SCHEMA_VERSION = '1';

/**
 * The numerical acceptance gate.
 *
 * "High precision" is not a gate — it is an adjective. These are the numbers a
 * change must beat before bulk approval may ship, and they live in the contract
 * so a threshold cannot be relaxed quietly in a service file.
 */
export const EXTRACTION_QUALITY_GATE = Object.freeze({
  /** Of the requirements proposed at HIGH confidence, the share that are correct.
   *  Highest bar because HIGH is what bulk-approve acts on without reading. */
  highRequirementPrecision: 0.95,
  /** Of the location requirements actually in the screenplay, the share found. */
  locationRequirementRecall: 0.9,
  /** Quotes that do not appear in the source text. Near-zero: a fabricated quote
   *  destroys trust in every quote. */
  unsupportedEvidenceQuoteRate: 0.01,
  /** Output that fails the schema or uses an out-of-vocabulary code. Must be
   *  exactly zero — the vocabulary is sent with the request. */
  invalidSchemaOrTaxonomyRate: 0,
  /** Responses needing the single structured-output repair attempt. */
  schemaRepairRate: 0.03,
});

export interface ExtractionEvalResult {
  readonly highRequirementPrecision: number;
  readonly locationRequirementRecall: number;
  readonly unsupportedEvidenceQuoteRate: number;
  readonly invalidSchemaOrTaxonomyRate: number;
  readonly schemaRepairRate: number;
}

export interface GateFailure {
  readonly metric: string;
  readonly observed: number;
  readonly required: number;
  readonly direction: 'at_least' | 'at_most';
}

/** Returns every failure rather than the first, so one eval run tells you
 *  everything that regressed instead of one thing at a time. */
export function evaluateQualityGate(result: ExtractionEvalResult): GateFailure[] {
  const failures: GateFailure[] = [];
  const gate = EXTRACTION_QUALITY_GATE;

  if (result.highRequirementPrecision < gate.highRequirementPrecision) {
    failures.push({
      metric: 'highRequirementPrecision',
      observed: result.highRequirementPrecision,
      required: gate.highRequirementPrecision,
      direction: 'at_least',
    });
  }
  if (result.locationRequirementRecall < gate.locationRequirementRecall) {
    failures.push({
      metric: 'locationRequirementRecall',
      observed: result.locationRequirementRecall,
      required: gate.locationRequirementRecall,
      direction: 'at_least',
    });
  }
  if (result.unsupportedEvidenceQuoteRate > gate.unsupportedEvidenceQuoteRate) {
    failures.push({
      metric: 'unsupportedEvidenceQuoteRate',
      observed: result.unsupportedEvidenceQuoteRate,
      required: gate.unsupportedEvidenceQuoteRate,
      direction: 'at_most',
    });
  }
  if (result.invalidSchemaOrTaxonomyRate > gate.invalidSchemaOrTaxonomyRate) {
    failures.push({
      metric: 'invalidSchemaOrTaxonomyRate',
      observed: result.invalidSchemaOrTaxonomyRate,
      required: gate.invalidSchemaOrTaxonomyRate,
      direction: 'at_most',
    });
  }
  if (result.schemaRepairRate > gate.schemaRepairRate) {
    failures.push({
      metric: 'schemaRepairRate',
      observed: result.schemaRepairRate,
      required: gate.schemaRepairRate,
      direction: 'at_most',
    });
  }

  return failures;
}
